import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { getJlgFirstAndLastEmiDates, calculateEmiAmountRaw } from "../../../utils/utils";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";

const JlgCollectionReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getJlgCollectionReportData`,
          { params: { id } }
        );
        // console.log(
        //   "getCollectionReportData: " + JSON.stringify(response.data)
        // );
        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    {
      label: "Center Name",
      render: (data) => data.fk_member_details_belongsTo_center_centerId.name,
    },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) =>
        `${data.permanentDoorNo},${data.permanentStreetNo},${data.permanentPanchayat},${data.permanentTaluk},${data.permanentDistrict}-${data.permanentPincode}`,
    },
    { label: "Contact No", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    {
      label: "Sub Category",
      render: (data) => data.businessDetails.natureOfBusiness,
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    { label: "Loan Account No", render: (data) => data.loanId },
    {
      label: "Rate of Interest",
      render: (data) => data.proposedLoanDetails.rateOfInterest,
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" &&
          data.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString(
            "en-US",
            {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }
          )
          : "",
    },
    {
      label: "EMI Date",
      render: (data) =>
        data.receiptEmiDate
          ? new Date(data.receiptEmiDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getJlgFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-US") : "";
      },
    },
    {
      label: "Loan Amount",
      render: (data) =>
        data.branchManagerStatus === "disbursed"
          ? data.sanctionedLoanAmountBySanctionCommittee
          : "",
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    { label: "Prospect ID", render: (data) => data.ApplicationId },
    {
      label: "Total Principal Paid",
      render: (data) => data.totalPrincipalPaid,
    },
    { label: "Total Interest Paid", render: (data) => data.totalInterestPaid },
    { label: "Total EMI Paid", render: (data) => data.totalEmiPaid },
    { label: "EMI Amount", render: (data) => data.receiptEmiAmount },
    { label: "Received Amount", render: (data) => data.receivedAmount },
    { label: "Description", render: (data) => data.receiptDescription },
    {
      label: "Receipt No",
      render: (data) => data.collectionApproval?.receiptNo || "",
    },
    {
      label: "Collected Date",
      render: (data) =>
        data.collectedDate
          ? new Date(data.collectedDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          : "",
    },
    {
      label: "Collected Time",
      render: (data) =>
        data.collectedDate
          ? new Date(data.collectedDate).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      label: "CRO Submitted Date",
      render: (data) =>
        data.collectionApproval?.fieldManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.fieldManagerStatusUpdatedAt
          ).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          : "",
    },
    {
      label: "CRO Submitted Time",
      render: (data) =>
        data.collectionApproval?.fieldManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.fieldManagerStatusUpdatedAt
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      label: "Branch Manager Approved Date",
      render: (data) =>
        data.collectionApproval?.branchManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.branchManagerStatusUpdatedAt
          ).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          : "",
    },
    {
      label: "Branch Manager Approved Time",
      render: (data) =>
        data.collectionApproval?.branchManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.branchManagerStatusUpdatedAt
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      label: "MIS Approved Date",
      render: (data) =>
        data.collectionApproval?.misStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.misStatusUpdatedAt
          ).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          : "",
    },
    {
      label: "MIS Approved Time",
      render: (data) =>
        data.collectionApproval?.misStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.misStatusUpdatedAt
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
    {
      label: "Accounts Manager Approved Date",
      render: (data) =>
        data.collectionApproval?.accountManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.accountManagerStatusUpdatedAt
          ).toLocaleDateString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          : "",
    },
    {
      label: "Accounts Manager Approved Time",
      render: (data) =>
        data.collectionApproval?.accountManagerStatusUpdatedAt
          ? new Date(
            data.collectionApproval?.accountManagerStatusUpdatedAt
          ).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
    },
  ];

  const branchOptions = [...new Set(reportData.map((data) => data?.branchName))]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [...new Set(reportData.map((data) => data?.username))]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const filterData = (data) => {
    let filteredData = data;

    // Check if both dates are selected
    if (fromDate && toDate) {
      filteredData = filteredData.filter((item) => {
        const emiDate = new Date(item.receiptEmiDate);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        return emiDate >= from && emiDate <= to;
      });
    } else {
      filteredData = []; // Return an empty array if no date is selected.
    }

    // Filter by selected branch
    if (selectedBranchFilter) {
      filteredData = filteredData.filter(
        (item) => item?.branchName === selectedBranchFilter.value
      );
    }

    // Filter by selected employee
    if (selectedEmployeeFilter) {
      filteredData = filteredData.filter(
        (item) => item?.username === selectedEmployeeFilter.value
      );
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filteredData = filteredData.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    // Calculate totals based on receiptsDetails for each item and attach to each item
    filteredData = filteredData.map((item) => {
      const expectedLoanAmount = item.sanctionedLoanAmountBySanctionCommittee;
      const tenureInMonths = item.proposedLoanDetails.tenureInMonths;
      const rateOfInterest = item.proposedLoanDetails.rateOfInterest / 100;
      const monthlyRate = rateOfInterest / 12;

      // Calculate the EMI amount using the amortization formula.
      // calculateEmiAmountRaw wants the annual rate as a plain percentage (e.g.
      // 26), not the /100 decimal form kept above for monthlyRate below -
      // pass the original field, not the local `rateOfInterest`.
      const emi = calculateEmiAmountRaw(
        expectedLoanAmount,
        item.proposedLoanDetails.rateOfInterest,
        tenureInMonths
      );

      let balance = expectedLoanAmount;
      let totalPrincipalPaid = 0;
      let totalInterestPaid = 0;
      let totalEmiPaid = 0;

      const to = new Date(toDate);

      // Group receipts by emiDate to handle multiple payments on the same date
      const receiptsByDate = item.receiptsDetails.reduce((acc, receipt) => {
        const date = receipt.emiDate;
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(receipt);
        return acc;
      }, {});

      // Iterate over each unique EMI date up to toDate
      Object.entries(receiptsByDate).forEach(([emiDate, receipts]) => {
        const emiDateObj = new Date(emiDate);
        if (emiDateObj > to) return; // Ignore dates after the specified toDate

        // Calculate the interest for the current balance
        const interestComponent = balance * monthlyRate;
        let totalReceivedForDate = receipts.reduce(
          (sum, receipt) => sum + receipt.receivedAmount,
          0
        );

        // Apply payments towards interest first, then principal
        if (totalReceivedForDate >= interestComponent) {
          totalInterestPaid += interestComponent;
          totalReceivedForDate -= interestComponent; // Remaining after covering interest

          // Apply the remaining amount to principal
          const principalPaid = Math.min(
            totalReceivedForDate,
            emi - interestComponent
          );
          totalPrincipalPaid += principalPaid;
          balance -= principalPaid;
        } else {
          // If payment does not cover full interest, add partial interest payment
          totalInterestPaid += totalReceivedForDate;
        }

        // Add the total EMI received for this date to totalEmiPaid
        totalEmiPaid += receipts.reduce(
          (sum, receipt) => sum + receipt.receivedAmount,
          0
        );
      });

      // This row IS the foreclosure settlement (see the synthetic row the
      // backend adds in getJlgCollectionReportData.js). Its own money is not
      // in item.receiptsDetails - a settlement lives only in
      // foreclosure_approval, never as a receipts row (bugfix.md Bug 89) -
      // so the loop above cannot see it. Add it in directly; item.receivedAmount
      // already carries the settled principal for this specific row.
      if (item.receiptDescription === "Foreclosure Settlement") {
        totalPrincipalPaid += item.receivedAmount || 0;
        totalEmiPaid += item.receivedAmount || 0;
      }

      // Attach calculated totals to the item
      return {
        ...item,
        totalPrincipalPaid: Math.round(totalPrincipalPaid),
        totalInterestPaid: Math.round(totalInterestPaid),
        totalEmiPaid: Math.round(totalEmiPaid),
      };
    });

    return filteredData;
  };

  const totalPages = Math.ceil(filterData(reportData).length / itemsPerPage);

  const filteredData = filterData(reportData).slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">JLG Collection Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={3} md={6} className="mb-2">
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDate && toDate && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={3} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDate && toDate && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={filteredData}
                reportName="CollectionReport"
                excelData={filterData(reportData)}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JlgCollectionReport;
